import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import {
  useAuth,
  invitationService,
  InvitationResponse,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@aaas/shared';

interface InvitationGeneratorProps {
  workspaceId: string;
  workspaceName: string;
  appUrl: string;
}

export const InvitationGenerator: React.FC<InvitationGeneratorProps> = ({
  workspaceId,
  workspaceName,
  appUrl,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation('portal');
  const [generating, setGenerating] = useState(false);
  const [invitation, setInvitation] = useState<InvitationResponse | null>(null);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [fullInvitationUrl, setFullInvitationUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Auto-generate invitation when component mounts
  useEffect(() => {
    if (user && workspaceId) {
      generateInvitation('member');
    }
  }, [user, workspaceId]);

  const generateInvitation = async (role: 'member' = 'member') => {
    if (!user) return;

    try {
      setGenerating(true);
      setError(null);

      const idToken = await user.getIdToken();
      const response = await invitationService.generateInvitation(
        workspaceId,
        { role, expires_in_hours: 3 },
        idToken
      );

      setInvitation(response);

      // Construct full URL for QR code (backend only returns path)
      const fullUrl = `${appUrl}${response.qr_code_data}`;
      setFullInvitationUrl(fullUrl);

      // Generate QR code image
      const qrDataUrl = await QRCode.toDataURL(fullUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
        errorCorrectionLevel: 'M',
      });

      setQrCodeImage(qrDataUrl);
    } catch (error: unknown) {
      console.error('Failed to generate invitation:', error);

      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { detail?: string; message?: string } };
        };
        if (axiosError.response?.status === 403) {
          setError(t('invitation.permissionDenied'));
        } else {
          setError(
            axiosError.response?.data?.message ||
              axiosError.response?.data?.detail ||
              t('invitation.generateFailed'),
          );
        }
      } else {
        setError(t('invitation.generateFailed'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const formatExpiryTime = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    return expiry.toLocaleString();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl">
      {/* Left Content */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>{t('invitation.cardTitle')}</span>
            </CardTitle>
            <CardDescription>
              {t('invitation.cardDescription', { workspaceName })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Member Role Info */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t('invitation.roleLabel')}</h3>
              <p className="text-sm text-gray-500">{t('invitation.roleDescription')}</p>
            </div>

            {/* Shareable Link */}
            {invitation && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('invitation.linkLabel')}</h3>
                <div className="flex">
                  <input
                    type="text"
                    value={fullInvitationUrl || ''}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-sm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(fullInvitationUrl || '')}
                    className="rounded-l-none border-l-0"
                  >
                    {copied ? t('invitation.copied') : t('invitation.copy')}
                  </Button>
                </div>
              </div>
            )}

            {/* Expiry Info */}
            {invitation && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-yellow-600 mt-0.5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-yellow-800 font-medium">{t('invitation.expiry')}</p>
                    <p className="text-xs text-yellow-700">
                      {formatExpiryTime(invitation.expires_at)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3">
              <Button
                onClick={() => generateInvitation('member')}
                disabled={generating}
                className="flex-1"
              >
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('invitation.generating')}
                  </>
                ) : (
                  t('invitation.generateNew')
                )}
              </Button>
              {invitation && (
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(fullInvitationUrl || '')}
                  className="flex-1"
                >
                  {copied ? t('invitation.copied') : t('invitation.copyLink')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right QR Code */}
      <div className="flex items-start justify-center">
        <Card className="w-full max-w-sm">
          <CardHeader>
            <CardTitle className="text-center">{t('invitation.qrHeading')}</CardTitle>
            <CardDescription className="text-center">{t('invitation.qrDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {generating ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm text-gray-600">{t('invitation.qrLoading')}</p>
                </div>
              </div>
            ) : qrCodeImage ? (
              <div className="text-center">
                <div className="inline-block p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <img src={qrCodeImage} alt={t('invitation.qrHeading')} className="w-64 h-64" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <svg
                    className="w-16 h-16 mx-auto mb-2 text-gray-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <p className="text-sm">{t('invitation.qrPlaceholder')}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
